import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';
import toast from 'react-hot-toast';
import { requiredMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

const errBorder = (msg) => (msg ? { borderColor: ERROR_COLOR } : {});

const CARD = {
  background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--border)',
  boxShadow: 'var(--card-shadow)', padding: '22px', marginBottom: '20px',
};
const LABEL = { display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' };
const INPUT = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.88rem', background: '#fff', color: 'var(--text-primary)', boxSizing: 'border-box' };
const SECTION_TITLE = { fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' };
const SECTION_DESC = { fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '4px 0 0', lineHeight: 1.5 };

function StatusBadge({ status, number }) {
  const STATUS_META = {
    connected: { label: 'Connecté', icon: '✅', bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
    connecting: { label: 'Connexion en cours...', icon: '⏳', bg: '#fef9c3', color: '#854d0e', border: '#fef08a' },
    qr_ready: { label: 'Scannez le QR code', icon: '📷', bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
    disconnected: { label: 'Déconnecté', icon: '⚪', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
    unreachable: { label: 'Service WhatsApp injoignable', icon: '⚠️', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
  };
  const meta = STATUS_META[status] || { label: status, icon: '', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '8px',
      background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
      borderRadius: '999px', padding: '6px 14px', fontSize: '0.83rem', fontWeight: 700,
    }}>
      <span>{meta.icon}</span>
      <span>{meta.label}</span>
      {number && <span style={{ fontWeight: 500, opacity: 0.85 }}>· {number}</span>}
    </div>
  );
}

function WhatsAppConnectionPanel({ churchId }) {
  const [state, setState] = useState({ status: 'disconnected', number: null, qr: null });
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const fetchQrcode = useCallback(async () => {
    if (!churchId) return;
    try {
      const res = await api.get('/notifications/whatsapp/connection/', { params: { action: 'qrcode', church_id: churchId } });
      setState(s => ({ ...s, status: res.data.status, number: res.data.number, qr: res.data.qr || null }));
    } catch { /* ignore, le prochain tick réessaiera */ }
  }, [churchId]);

  const fetchStatus = useCallback(async () => {
    if (!churchId) return;
    try {
      const res = await api.get('/notifications/whatsapp/connection/', { params: { action: 'status', church_id: churchId } });
      setState(s => ({ ...s, status: res.data.status, number: res.data.number }));
      if (res.data.status === 'qr_ready') fetchQrcode();
    } catch {
      setState(s => ({ ...s, status: 'unreachable' }));
    }
  }, [churchId, fetchQrcode]);

  useEffect(() => {
    setState({ status: 'disconnected', number: null, qr: null });
    if (churchId) fetchStatus();
    return () => clearInterval(pollRef.current);
  }, [churchId, fetchStatus]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (state.status === 'connecting' || state.status === 'qr_ready') {
      pollRef.current = setInterval(fetchQrcode, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [state.status, fetchQrcode]);

  const handleConnect = async () => {
    if (!churchId) return;
    setLoading(true);
    try {
      await api.post('/notifications/whatsapp/connection/', { action: 'connect', church_id: churchId });
      toast.success('Connexion en cours — scannez le QR code.');
      await fetchQrcode();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Impossible de démarrer la connexion.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!churchId) return;
    setLoading(true);
    try {
      await api.post('/notifications/whatsapp/connection/', { action: 'disconnect', church_id: churchId });
      toast.success('WhatsApp déconnecté.');
      setState({ status: 'disconnected', number: null, qr: null });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la déconnexion.');
    } finally {
      setLoading(false);
    }
  };

  if (!churchId) {
    return (
      <div style={CARD}>
        <h2 style={SECTION_TITLE}>📱 Connexion WhatsApp</h2>
        <p style={SECTION_DESC}>Sélectionnez une église ci-dessus pour gérer sa connexion WhatsApp.</p>
      </div>
    );
  }

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        <div>
          <h2 style={SECTION_TITLE}>📱 Connexion WhatsApp</h2>
          <p style={SECTION_DESC}>Chaque église connecte son propre numéro en scannant son propre QR code.</p>
        </div>
        <StatusBadge status={state.status} number={state.number} />
      </div>

      {state.status === 'qr_ready' && (
        <div style={{ textAlign: 'center', marginBottom: '16px', padding: '20px', background: '#f8fafc', borderRadius: '10px', border: '1px solid var(--border)' }}>
          {state.qr ? (
            <img src={state.qr} alt="QR code WhatsApp" style={{ width: '220px', height: '220px', borderRadius: '8px', border: '1px solid var(--border)', background: '#fff' }} />
          ) : (
            <div style={{ width: '220px', height: '220px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              Génération du QR code...
            </div>
          )}
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '12px', marginBottom: 0 }}>
            Ouvrez WhatsApp sur le téléphone à connecter → Paramètres → Appareils liés → Lier un appareil
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        {state.status !== 'connected' && (
          <button onClick={handleConnect} disabled={loading}
            style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, fontSize: '0.85rem', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? '...' : 'Connecter'}
          </button>
        )}
        {state.status === 'connected' && (
          <button onClick={handleDisconnect} disabled={loading}
            style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, fontSize: '0.85rem', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? '...' : 'Déconnecter'}
          </button>
        )}
      </div>
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: 'nouveau', label: 'Nouveau' },
  { value: 'membre', label: 'Membre régulier' },
  { value: 'serviteur', label: 'Serviteur' },
  { value: 'invite', label: 'Invité' },
];

const EMPTY_RULE = { label: '', offset_value: 1, offset_unit: 'days', message_template: 'Bonjour {prenom}, rappel : {activite} aura lieu le {date} à {heure}.', target_statuses: [], voice: false, active: true };

function formatOffset(value, unit) {
  const n = Number(value);
  if (unit === 'minutes') return `${n} minute${n > 1 ? 's' : ''}`;
  return `${n} jour${n > 1 ? 's' : ''}`;
}

function WhatsAppRemindersPanel({ isSuperAdmin, churchId }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchRules = useCallback(async () => {
    if (isSuperAdmin && !churchId) { setRules([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = isSuperAdmin ? { church_id: churchId } : {};
      const res = await api.get('/notifications/whatsapp/reminder-rules/', { params });
      setRules(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, churchId]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const toggleFormStatus = (value) => {
    setForm(f => ({
      ...f,
      target_statuses: f.target_statuses.includes(value) ? f.target_statuses.filter(s => s !== value) : [...f.target_statuses, value],
    }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_RULE);
    setShowForm(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      label: rule.label,
      offset_value: rule.offset_value,
      offset_unit: rule.offset_unit,
      message_template: rule.message_template,
      target_statuses: rule.target_statuses || [],
      voice: rule.voice,
      active: rule.active,
    });
    setShowForm(true);
  };

  const [ruleErrors, setRuleErrors] = useState({});

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!form.label.trim()) errors.label = requiredMessage('Libellé');
    if (form.offset_value === '' || form.offset_value === null || form.offset_value === undefined) errors.offset_value = requiredMessage("Délai avant l'activité");
    setRuleErrors(errors);
    if (Object.keys(errors).length) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (isSuperAdmin) payload.church_id = churchId;
      if (editingId) {
        await api.patch(`/notifications/whatsapp/reminder-rules/${editingId}/`, payload);
        toast.success('Règle mise à jour.');
      } else {
        await api.post('/notifications/whatsapp/reminder-rules/', payload);
        toast.success('Règle de rappel créée.');
      }
      setForm(EMPTY_RULE);
      setEditingId(null);
      setShowForm(false);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de l'enregistrement de la règle.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule) => {
    try {
      await api.patch(`/notifications/whatsapp/reminder-rules/${rule.id}/`, { active: !rule.active });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, active: !r.active } : r));
    } catch {
      toast.error('Erreur lors de la mise à jour.');
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Supprimer la règle "${rule.label}" ?`)) return;
    try {
      await api.delete(`/notifications/whatsapp/reminder-rules/${rule.id}/`);
      setRules(prev => prev.filter(r => r.id !== rule.id));
      toast.success('Règle supprimée.');
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  if (isSuperAdmin && !churchId) {
    return (
      <div style={CARD}>
        <h2 style={SECTION_TITLE}>⏰ Rappels automatiques</h2>
        <p style={SECTION_DESC}>Sélectionnez une église ci-dessus pour configurer ses rappels automatiques.</p>
      </div>
    );
  }

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '4px' }}>
        <div>
          <h2 style={SECTION_TITLE}>⏰ Rappels automatiques</h2>
          <p style={SECTION_DESC}>
            Envoie automatiquement un message WhatsApp aux fidèles avant chaque activité programmée<br />
            (ex. 30 jours, 5 jours, 1 jour ou 60 minutes avant — délai entièrement libre).
          </p>
        </div>
        <button onClick={() => (showForm ? (setShowForm(false), setEditingId(null)) : openCreate())}
          style={{ background: showForm ? '#f1f5f9' : '#25D366', color: showForm ? '#475569' : '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {showForm ? 'Annuler' : '+ Nouvelle règle'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmitForm} noValidate style={{ background: '#f8fafc', borderRadius: '10px', padding: '18px', margin: '16px 0', border: '1px solid var(--border)' }}>
          <div style={{ marginBottom: '14px' }}>
            <label style={LABEL}>Libellé<Req /></label>
            <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="Ex : Rappel 5 jours avant"
              style={{ ...INPUT, ...errBorder(ruleErrors.label) }} />
            <FieldError message={ruleErrors.label} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '12px', marginBottom: '14px' }}>
            <div>
              <label style={LABEL}>Délai avant l'activité<Req /></label>
              <input type="number" min={0} value={form.offset_value} onChange={e => setForm(f => ({ ...f, offset_value: e.target.value }))}
                style={{ ...INPUT, ...errBorder(ruleErrors.offset_value) }} />
              <FieldError message={ruleErrors.offset_value} />
            </div>
            <div>
              <label style={LABEL}>Unité</label>
              <select value={form.offset_unit} onChange={e => setForm(f => ({ ...f, offset_unit: e.target.value }))} style={INPUT}>
                <option value="days">Jour(s)</option>
                <option value="minutes">Minute(s)</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={LABEL}>Message</label>
            <textarea value={form.message_template} onChange={e => setForm(f => ({ ...f, message_template: e.target.value }))} rows={3}
              style={{ ...INPUT, resize: 'vertical' }} />
            <p style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
              Variables disponibles : <code>{'{prenom} {nom} {activite} {date} {heure} {lieu}'}</code>
            </p>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={LABEL}>Statuts ciblés (vide = tous)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
              {STATUS_OPTIONS.map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.83rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.target_statuses.includes(opt.value)} onChange={() => toggleFormStatus(opt.value)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.83rem', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer', marginBottom: '16px' }}>
            <input type="checkbox" checked={form.voice} onChange={e => setForm(f => ({ ...f, voice: e.target.checked }))} />
            Envoyer aussi un message vocal
          </label>

          <button type="submit" disabled={saving}
            style={{ background: '#1e40af', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, fontSize: '0.85rem', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Enregistrement...' : (editingId ? 'Enregistrer les modifications' : 'Créer la règle')}
          </button>
        </form>
      )}

      <div style={{ marginTop: '16px' }}>
        {loading ? (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Chargement...</p>
        ) : rules.length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Aucune règle de rappel configurée.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {rules.map(rule => (
              <div key={rule.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
                padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border)',
                background: rule.active ? '#fff' : '#f8fafc', opacity: rule.active ? 1 : 0.6,
              }}>
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>{rule.label}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    {formatOffset(rule.offset_value, rule.offset_unit)} avant{rule.voice ? ' · vocal' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button onClick={() => toggleActive(rule)}
                    style={{ background: rule.active ? '#dcfce7' : '#f1f5f9', color: rule.active ? '#166534' : '#64748b', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                    {rule.active ? 'Actif' : 'Inactif'}
                  </button>
                  <button onClick={() => openEdit(rule)}
                    style={{ background: '#eff6ff', color: '#1e40af', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                    Modifier
                  </button>
                  <button onClick={() => deleteRule(rule)}
                    style={{ background: '#fef2f2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const EMPTY_FORM = { message: '', target_type: 'church', church_id: '', statuses: [], voice: false };

export default function NotificationsWhatsApp() {
  const { user } = useAuth();
  const { activeChurchId, canSwitchChurch, churches } = useChurch();
  const isSuperAdmin = user?.role === 'superadmin_platform';
  const isSupervisor = user?.role === 'superviseur';

  const [form, setForm] = useState({ ...EMPTY_FORM, target_type: isSuperAdmin ? 'all' : 'church' });
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // L'église pilotée par la connexion WhatsApp/rappels suit le sélecteur
  // global (superadmin ou superviseur) ; un admin classique reste sur la sienne.
  const whatsappChurchId = canSwitchChurch ? activeChurchId : (user?.church_id || '');

  const handleChange = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const toggleStatus = (value) => {
    setForm(f => ({
      ...f,
      statuses: f.statuses.includes(value) ? f.statuses.filter(s => s !== value) : [...f.statuses, value],
    }));
  };

  const [sendErrors, setSendErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!form.message.trim()) errors.message = requiredMessage('Message');
    if (isSuperAdmin && form.target_type === 'church' && !form.church_id) errors.church_id = requiredMessage('Église');
    setSendErrors(errors);
    if (Object.keys(errors).length) return;
    if (isSupervisor && !whatsappChurchId) {
      toast.error('Veuillez choisir une église à gérer.');
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const payload = {
        message: form.message,
        target_type: form.target_type,
        statuses: form.statuses,
        voice: form.voice,
      };
      if (isSuperAdmin && form.target_type === 'church') payload.church_id = form.church_id;
      if (isSupervisor) payload.church_id = whatsappChurchId;
      const res = await api.post('/notifications/whatsapp/broadcast/', payload);
      toast.success(res.data.message || 'Message WhatsApp envoyé.');
      setLastResult(res.data);
      setForm(f => ({ ...EMPTY_FORM, target_type: f.target_type }));
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '760px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
          Notifications WhatsApp
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.9rem' }}>
          Connexion, rappels automatiques et envoi manuel de messages WhatsApp aux fidèles.
        </p>
      </div>

      {canSwitchChurch && (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '14px' }}>
          Église pilotée : <strong>{churches.find(c => String(c.id) === String(activeChurchId))?.name || '— sélectionnez une église dans la barre du haut —'}</strong>
        </div>
      )}

      <WhatsAppConnectionPanel churchId={whatsappChurchId} />

      <WhatsAppRemindersPanel isSuperAdmin={canSwitchChurch} churchId={whatsappChurchId} />

      <div style={CARD}>
        <h2 style={{ ...SECTION_TITLE, marginBottom: '16px' }}>💬 Envoi manuel</h2>
        <form onSubmit={handleSubmit} noValidate>
          <div style={{ marginBottom: '14px' }}>
            <label style={LABEL}>Message<Req /></label>
            <textarea value={form.message} onChange={handleChange('message')} rows={4}
              placeholder="Bonjour, nous vous rappelons que..."
              style={{ ...INPUT, resize: 'vertical', ...errBorder(sendErrors.message) }} />
            <FieldError message={sendErrors.message} />
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.voice} onChange={e => setForm(f => ({ ...f, voice: e.target.checked }))} />
              Envoyer aussi un message vocal (texte lu à voix haute)
            </label>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={LABEL}>Statuts ciblés (vide = tous les fidèles)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
              {STATUS_OPTIONS.map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.statuses.includes(opt.value)} onChange={() => toggleStatus(opt.value)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {isSuperAdmin && (
            <div style={{ marginBottom: '14px' }}>
              <label style={LABEL}>Destinataires</label>
              <select value={form.target_type} onChange={handleChange('target_type')} style={INPUT}>
                <option value="all">Toute la plateforme</option>
                <option value="church">Une église spécifique</option>
              </select>
            </div>
          )}

          {isSuperAdmin && form.target_type === 'church' && (
            <div style={{ marginBottom: '14px' }}>
              <label style={LABEL}>Église<Req /></label>
              <select value={form.church_id} onChange={handleChange('church_id')} style={{ ...INPUT, ...errBorder(sendErrors.church_id) }}>
                <option value="">— Choisir une église —</option>
                {churches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <FieldError message={sendErrors.church_id} />
            </div>
          )}

          {isSupervisor && (
            <div style={{ marginBottom: '14px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Envoyé à : <strong>{churches.find(c => String(c.id) === String(whatsappChurchId))?.name || '— sélectionnez une église dans la barre du haut —'}</strong>
            </div>
          )}

          <button type="submit" disabled={sending}
            style={{
              background: '#25D366', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 22px', fontWeight: 700, fontSize: '0.9rem', cursor: sending ? 'default' : 'pointer',
              opacity: sending ? 0.7 : 1,
            }}>
            {sending ? 'Envoi...' : 'Envoyer sur WhatsApp'}
          </button>
        </form>

        {lastResult && (
          <div style={{ marginTop: '16px', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', fontSize: '0.86rem', color: 'var(--text-primary)' }}>
            <strong>Dernier envoi :</strong> {lastResult.sent} réussi(s){lastResult.failed ? `, ${lastResult.failed} échec(s)` : ''}.
          </div>
        )}
      </div>
    </div>
  );
}
